import { HAZARD_CATEGORIES } from "encamp-shared/src/hazards/hazardCategories.generated";
import { legacyHazardsFromCategories } from "encamp-shared/src/hazards/legacyHazardMapping";
import { HazardType } from "generated-graphql/graphql";
import { isEqual, sortBy } from "lodash-es";
import {
  HealthHazardLabels,
  HealthHazardOrder,
  PhysicalHazardLabels,
  PhysicalHazardOrder,
} from "util/hazards";

import { ChemicalDetailInputs } from ".";

type LegacyHazardLabels = { physical: string[]; health: string[] };

function labelsInCheckboxOrder(
  physicalHazards: readonly string[],
  healthHazards: readonly string[]
): LegacyHazardLabels {
  const physical = new Set(physicalHazards);
  const health = new Set(healthHazards);
  return {
    physical: PhysicalHazardOrder.filter((hazard) => physical.has(hazard)).map(
      (hazard) => PhysicalHazardLabels[hazard]
    ),
    health: HealthHazardOrder.filter((hazard) => health.has(hazard)).map(
      (hazard) => HealthHazardLabels[hazard]
    ),
  };
}

/** Labels of the legacy hazards the picked categories save as, in checkbox order. */
export function legacyHazardLabelsFor(codes: readonly string[]): LegacyHazardLabels {
  const derived = legacyHazardsFromCategories(
    HAZARD_CATEGORIES.filter((row) => codes.includes(row.code))
  );
  return labelsInCheckboxOrder(derived.physicalHazards, derived.healthHazards);
}

export type LegacyHazardSeed = Pick<
  ChemicalDetailInputs,
  "physicalHazards" | "healthHazards" | "physicalHazardCategoryCodes" | "healthHazardCategoryCodes"
>;

/**
 * Labels of the legacy hazards a save leaves the chemical with: its stored ones
 * while it has no categories and none are picked (the save omits the codes),
 * otherwise those the picked categories derive.
 */
export function reportedLegacyHazardLabels(
  picked: readonly string[],
  seed: LegacyHazardSeed | undefined
): LegacyHazardLabels {
  const storedCodes = [
    ...(seed?.physicalHazardCategoryCodes ?? []),
    ...(seed?.healthHazardCategoryCodes ?? []),
  ];
  if (picked.length === 0 && storedCodes.length === 0) {
    return labelsInCheckboxOrder(seed?.physicalHazards ?? [], seed?.healthHazards ?? []);
  }
  return legacyHazardLabelsFor(picked);
}

type StoredHazardCategory = { code: string; hazardType: HazardType };

type HazardCategoryCodeFields = Pick<
  ChemicalDetailInputs,
  "healthHazardCategoryCodes" | "physicalHazardCategoryCodes"
>;

/**
 * Form values for the two hazard pickers. Undefined when the pickers are not
 * shown, so the save input omits the list and the server leaves hazard rows alone.
 */
export function seedHazardCategoryCodes(
  chemical: { hazardCategories?: readonly StoredHazardCategory[] | null } | undefined,
  usesHazardCategories: boolean
): HazardCategoryCodeFields {
  if (!usesHazardCategories) {
    return { healthHazardCategoryCodes: undefined, physicalHazardCategoryCodes: undefined };
  }
  const codesOf = (hazardType: HazardType) =>
    (chemical?.hazardCategories ?? [])
      .filter((category) => category.hazardType === hazardType)
      .map((category) => category.code);
  return {
    healthHazardCategoryCodes: codesOf(HazardType.Health),
    physicalHazardCategoryCodes: codesOf(HazardType.Physical),
  };
}

const sameCodes = (a: readonly string[] | undefined, b: readonly string[] | undefined) =>
  isEqual(sortBy(a ?? []), sortBy(b ?? []));

/**
 * Drops both picker fields when neither selection differs from the seeded
 * values, so the save input omits `hazardCategoryCodes` and an unrelated edit
 * cannot rewrite the chemical's hazards.
 */
export function omitUntouchedHazardCategoryCodes<T extends HazardCategoryCodeFields>(
  values: T,
  seed: HazardCategoryCodeFields | undefined
): T {
  const untouched =
    sameCodes(values.healthHazardCategoryCodes, seed?.healthHazardCategoryCodes) &&
    sameCodes(values.physicalHazardCategoryCodes, seed?.physicalHazardCategoryCodes);
  if (!untouched) return values;
  return {
    ...values,
    healthHazardCategoryCodes: undefined,
    physicalHazardCategoryCodes: undefined,
  };
}
